import { Card } from 'antd';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import BybitForm from '@/components/SearchTablePage/BybitForm';
import { FormType } from '@/components/SearchTablePage/types/tplTypes';
import { BybitFormRef } from '@/components/SearchTablePage/utils/commonInterface';
import { IParentPermission } from '@/services/types/apiManagement';

import { DisclaimerModal } from '../BlockTradeDisclaimer';
import { ICreateApi, ISearchCreateApi, useCreateApiHelper } from './helper';
import styles from './index.module.less';

export interface IApiDetail {
  readOnly: boolean;
  bindIps: string;
  permissions: number[];
}

const CreateApiForm = (
  {
    apiDetail,
    updateOpenApiForm,
    onValuesChange,
  }: {
    apiDetail: IApiDetail;
    updateOpenApiForm: boolean;
    onValuesChange: (values: IApiDetail) => void;
  },
  ref: any,
) => {
  const { getFormItems } = useCreateApiHelper();
  const formRef = useRef<BybitFormRef>();
  const [readOnly, setReadOnly] = useState<boolean>(true);
  const [formValues, setFormValues] = useState<ICreateApi>({} as ICreateApi);
  const [disclaimerVisible, setDisclaimerVisible] = useState<boolean>(false);

  const onReadOnlyChange = (e: RadioChangeEvent) => {
    const value = e?.target?.value;
    setReadOnly(value);
  };

  const getFormProps = () => {
    return {
      onFormRef: formRef,
      configData: {
        query: getFormItems(readOnly, onReadOnlyChange),
      },
      formType: FormType.Form,
      initialValues: apiDetail,
      handleSearch: createApi,
    };
  };

  useEffect(() => {
    if (updateOpenApiForm) {
      formRef.current?.getForm()?.resetFields();
    }
  }, [apiDetail, updateOpenApiForm]);

  useImperativeHandle(ref, () => ({
    getOpenApiFormValues: () => {
      const form = formRef?.current?.getForm();
      return form?.getFieldsValue();
    },
    validateOpenApiForm: () => {
      const form = formRef?.current?.getForm();
      return form?.validateFields();
    },
  }));

  // const toggleIsIpRight = (e: boolean) => {
  //   onChangeIpRight(e);
  // };

  const createApi = (formValue: Partial<ICreateApi>) => {
    const { permissions: beforePermission } = formValue;
    const permissions = { ...beforePermission };
    // 勾选大宗交易，弹框显示免责声明
    const hasParentPermission =
      Array.isArray((permissions as IParentPermission)?.BlockTrade) &&
      (permissions as IParentPermission)?.BlockTrade.includes('BlockTrade');
    // 增加子账号勾选大宗交易，弹框提示免责声明
    const hasSubAccountPermission =
      Array.isArray((permissions as any)?.blockTrade) &&
      (permissions as any)?.blockTrade.includes('API_PERMISSION_BLOCK_TRADE');

    if (hasParentPermission || hasSubAccountPermission) {
      setDisclaimerVisible(true);
      setFormValues({ ...formValue, permissions } as ICreateApi);
      return;
    }
    setFormValues({ ...formValue, permissions } as ICreateApi);
  };

  const onCancel = () => {
    setDisclaimerVisible(false);
    setFormValues({} as ICreateApi);
  };

  const disclaimerConfirm = () => {
    setDisclaimerVisible(false);
  };

  return (
    <Card
      className={styles['create-api-form']}
      bordered={false}
      headStyle={{ borderBottomColor: 'var(--bds-gray-ele-border)' }}
    >
      <BybitForm<ICreateApi, ISearchCreateApi>
        formProps={{
          layout: 'vertical',
          scrollToFirstError: true,
          onValuesChange(changedValues, values) {
            onValuesChange(values);
          },
        }}
        {...getFormProps()}
      />
      <DisclaimerModal
        visible={disclaimerVisible}
        onConfirm={disclaimerConfirm}
        onCancel={onCancel}
      />
    </Card>
  );
};

export default forwardRef(CreateApiForm);
